import React, { useState, useEffect } from "react";
// LoginScreen extracted from App.jsx (C1 refactor, behavior-preserving). This is the
// auth-gate screen: App.jsx renders <LoginScreen> when there is no active user. The
// component body is moved verbatim (no logic rewrite). Default export to match the
// sibling auth component TwoFAScreen and App.jsx's `<LoginScreen onLogin=... users=...>`
// render. Internal-only (0 external consumers) → no barrel re-export.
//
// Dependencies (imported from their owning modules, never from App.jsx — would cycle):
//   • API_BASE → ../lib/endpoints.js — base URL for the /health + /login fetches.
//   • setServerOnline → ../lib/connection-state.js — flips the shared online flag once
//     the /health probe succeeds (read elsewhere by the app's connection state).
//   • hashPassword → ../utils.js — client-side SHA-256 of the password before POST.
//   • INITIAL_USERS → ../lib/initial-users.js — offline-login seed list (TRY 2 fallback).
//   • setSessionToken → ../utils/session.js — persists the session token on success.
//   • TwoFAScreen → ./TwoFAScreen.jsx — rendered for the 2FA challenge step.
//   • renderTelegramNotInitiated → ./TelegramNotInitiated.jsx — special-cases the
//     "__TELEGRAM_NOT_INITIATED__" error string into a friendly banner.
//   • globalTableCSS + mobileCSS → ../styles/{global-table,mobile}-css.js — inlined CSS.
//   • I → ../icons.jsx — custom SVG icon set (logo).
//   • VERSION → ../version.js — frontend version shown in the card + debug panel.
import { API_BASE } from '../lib/endpoints.js';
import { setServerOnline } from '../lib/connection-state.js';
import { hashPassword } from '../utils.js';
import { INITIAL_USERS } from '../lib/initial-users.js';
import { setSessionToken } from '../utils/session.js';
import TwoFAScreen from './TwoFAScreen.jsx';
import { renderTelegramNotInitiated } from './TelegramNotInitiated.jsx';
import { globalTableCSS } from '../styles/global-table-css.js';
import { mobileCSS } from '../styles/mobile-css.js';
import { I } from '../icons.jsx';
import { VERSION } from '../version.js';

function LoginScreen({ onLogin, users }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(0);
  const [twoFAState, setTwoFAState] = useState(null);
  const [serverStatus, setServerStatus] = useState("checking");
  const [debugData, setDebugData] = useState(null);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      while (!cancelled) {
        try {
          const res = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(3000) });
          if (res.ok) { setServerStatus("online"); setServerOnline(true); return; }
            else if (res.status === 403) { setServerStatus("banned"); return; }
          else setServerStatus("offline");
        } catch { setServerStatus("offline"); }
        if (!cancelled) await new Promise(r => setTimeout(r, 5000));
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (blocked > 0) return;
    setLoading(true); setError(""); setDebugData(null);

    const passwordClean = password.trim(); // FIX: trim whitespace before hashing
    const hashed = hashPassword(passwordClean);
    const emailClean = email.toLowerCase().trim();

    // ── TRY 1: Server login ──
    let serverWorked = false;
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // 2026-08-07: без credentials метка доверенного устройства (httpOnly-cookie
        // blitz_device) не уедет на сервер в локальной разработке — там фронт на :5173,
        // API на :3001, то есть запрос кросс-оригинный. На проде API_BASE относительный
        // и хватило бы дефолта, но 'include' корректен в обоих случаях.
        credentials: 'include',
        body: JSON.stringify({ email: emailClean, passwordHash: hashed }),
        signal: AbortSignal.timeout(8000),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch {
        console.log("⚠️ Server returned non-JSON:", text.substring(0, 200));
        throw new Error("non-json");
      }

      // 2FA check
      if (data && (data.requires2FA || data.require2FA)) { setLoading(false); setTwoFAState({twoFAToken: data.twoFAToken}); return; }
      // v31.42 H1 fix: backend sends 'no_telegram_setup' (see server.cjs login endpoint), frontend
      // check был `no_telegram` — mismatch → ветка не срабатывала → ниже показывался generic
      // "Invalid email or password. If you didn't change..." misleading hint. Accept both coded
      // для backward compat (если старый server deployed — fallback сработает).
      if (data && (data.error === 'no_telegram_setup' || data.error === 'no_telegram')) {
        setError(data.message || "Your account has no Telegram ID set for 2FA. Contact your administrator.");
        setLoading(false); return;
      }
      // v31.01: user has telegramChatId but hasn't started the bot yet
      // v31.02: fallback updated to current bot (bot migration 2026-04-17)
      if (data && data.error === 'TELEGRAM_NOT_INITIATED') {
        const bot = data.botUsername || 'blit_finance_bot';
        setError('__TELEGRAM_NOT_INITIATED__' + bot);
        setLoading(false);
        return;
      }

      serverWorked = true;

      if (res.ok && data.ok) {
        if (data.token) setSessionToken(data.token);
        onLogin(data.user);
        setLoading(false);
        return;
      } else if (res.status === 429 && data.error === "blocked") {
        // 2026-04-18: HIGH #3 frontend sync — backend перестал слать {minutes: N} ещё 2026-04-17
        // (commit d03bd039), т.к. banIP() делает PERMANENT blacklist, не 10/15 min timeout.
        // Раньше frontend врал "15 min" + фейковый countdown. Теперь — honest backend message.
        setBlocked(1);  // disable Sign In button (value не используется для UI text)
        setError(data.message || "IP banned due to too many failed login attempts. Contact admin to unblock.");
        if (data.debug) setDebugData(data.debug);
        setLoading(false);
        return;
      } else if (res.status === 401) {
        // v31.42 Fix #4: hint для users которые "точно помнят" пароль но он не работает.
        // Frequent scenario: admin reset + user не знает. Направляем к admin чтобы не банились.
        setError("Invalid email or password. If you didn't change it recently, ask admin to reset.");
        if (data.debug) setDebugData({ ...data.debug, clientHash8: hashed.substring(0, 8), clientHashFull: hashed });
        setLoading(false);
        return;
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (fetchErr) {
      // ── TRY 2: Offline login using INITIAL_USERS ──
      const u = INITIAL_USERS.find(u => u.email === emailClean && u.passwordHash === hashed);
      if (u) {
        console.log("✅ Offline login success for:", emailClean);
        onLogin({ email: u.email, name: u.name, pageAccess: u.pageAccess });
      } else {
        const emailExists = INITIAL_USERS.some(u => u.email === emailClean);
        if (emailExists) {
          setError(serverWorked ? "Invalid email or password. If you didn't change it recently, ask admin to reset." : "Server unreachable. Password doesn't match offline records.");
        } else {
          setError(serverWorked ? "Invalid email or password. If you didn't change it recently, ask admin to reset." : "Server unreachable. This account requires server access to login.");
        }
      }
    }
    setLoading(false);
  };

  const diagStyle = { background: "#FEF3C7", border: "1px solid #F59E0B40", borderRadius: 10, padding: 14, marginTop: 12, fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: "#92400E", lineHeight: 1.8, whiteSpace: "pre-wrap", maxHeight: 300, overflowY: "auto" };

  // Floating decoration SVGs
  const BitcoinSVG = ({ style }) => (
    <svg style={{ position: "absolute", pointerEvents: "none", filter: "drop-shadow(0 4px 12px rgba(247,181,0,0.3))", ...style }} width="90" height="90" viewBox="0 0 100 100">
      <defs><linearGradient id="btcg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#FFD700"/><stop offset="50%" stopColor="#F7B500"/><stop offset="100%" stopColor="#E5A000"/></linearGradient></defs>
      <circle cx="50" cy="50" r="46" fill="url(#btcg)" stroke="#D4940080" strokeWidth="3"/>
      <circle cx="50" cy="50" r="38" fill="none" stroke="#FFF4" strokeWidth="1.5"/>
      <text x="50" y="66" textAnchor="middle" fill="#FFF" fontSize="42" fontWeight="800" fontFamily="Arial,sans-serif" style={{textShadow:"0 2px 4px rgba(0,0,0,0.2)"}}>₿</text>
    </svg>
  );

  const AirpodsSVG = ({ style }) => (
    <svg style={{ position: "absolute", pointerEvents: "none", filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.1))", ...style }} width="85" height="100" viewBox="0 0 85 100">
      {/* Left airpod */}
      <ellipse cx="25" cy="32" rx="14" ry="18" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5"/>
      <ellipse cx="25" cy="28" rx="5" ry="5" fill="#E2E8F0"/>
      <rect x="22" y="48" width="6" height="32" rx="3" fill="#F1F5F9" stroke="#E2E8F0" strokeWidth="1"/>
      {/* Right airpod */}
      <ellipse cx="58" cy="26" rx="14" ry="18" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5"/>
      <ellipse cx="58" cy="22" rx="5" ry="5" fill="#E2E8F0"/>
      <rect x="55" y="42" width="6" height="35" rx="3" fill="#F1F5F9" stroke="#E2E8F0" strokeWidth="1"/>
      {/* Cute face on right one */}
      <circle cx="53" cy="24" r="1.8" fill="#334155"/>
      <circle cx="63" cy="24" r="1.8" fill="#334155"/>
      <ellipse cx="58" cy="30" rx="4" ry="3" fill="none" stroke="#334155" strokeWidth="1.2" strokeLinecap="round" style={{strokeDasharray:"0 7 7 0"}}/>
    </svg>
  );

  const UkraineFlagSVG = ({ style }) => (
    <svg style={{ position: "absolute", pointerEvents: "none", filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.12))", ...style }} width="80" height="65" viewBox="0 0 80 65">
      {/* Pole */}
      <rect x="8" y="5" width="3.5" height="58" rx="1.5" fill="#B0B8C4"/>
      <circle cx="9.75" cy="6" r="3.5" fill="#F59E0B"/>
      {/* Flag body with wave */}
      <path d="M13 10 Q35 6 55 12 Q70 16 75 13 L75 35 Q60 31 55 35 Q35 41 13 37 Z" fill="#0057B8"/>
      <path d="M13 37 Q35 41 55 35 Q60 31 75 35 L75 55 Q60 52 55 56 Q35 62 13 58 Z" fill="#FFD700"/>
    </svg>
  );

  // Sparkle/star decorations
  const Sparkle = ({ x, y, size, color, delay }) => (
    <div style={{ position: "absolute", left: x, top: y, pointerEvents: "none", animation: `sparkle 2s ease-in-out ${delay || 0}s infinite` }}>
      <svg width={size || 14} height={size || 14} viewBox="0 0 24 24">
        <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5Z" fill={color || "#38BDF8"} opacity="0.6"/>
      </svg>
    </div>
  );

  const Dot = ({ x, y, size, color }) => (
    <div style={{ position: "absolute", left: x, top: y, width: size || 8, height: size || 8, borderRadius: "50%", background: color || "#38BDF8", opacity: 0.4, pointerEvents: "none", animation: `float 3s ease-in-out infinite` }} />
  );

  if (twoFAState) return <TwoFAScreen twoFAToken={twoFAState.twoFAToken} onSuccess={j => { if(j.token) setSessionToken(j.token); onLogin(j.user||j); }} onBack={() => { setTwoFAState(null); setLoading(false); }} />;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg, #0A0F1E 0%, #0F172A 30%, #1E1B4B 60%, #0A0F1E 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", position: "relative", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <style>{globalTableCSS}{mobileCSS}{`
        @keyframes sparkle { 0%,100% { opacity:0.3; transform:scale(0.8) rotate(0deg); } 50% { opacity:1; transform:scale(1.2) rotate(180deg); } }
        @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
        @media (prefers-reduced-motion: reduce) {
  /* FIX 14: Battery drain on mobile — disable decorative animations when user prefers reduced motion */
  * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
}
@keyframes floatSlow { 0%,100% { transform:translateY(0) rotate(-5deg); } 50% { transform:translateY(-12px) rotate(5deg); } }
        @keyframes fadeSlideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(14,165,233,0.15); } 50% { box-shadow: 0 0 0 12px rgba(14,165,233,0); } }
      `}</style>

      {/* ══ Background decorations ══ */}
      {/* Soft gradient orbs */}
      <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%)", top: "-15%", right: "-10%", pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(167,139,250,0.05) 0%, transparent 70%)", bottom: "-10%", left: "-8%", pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(251,191,36,0.04) 0%, transparent 70%)", top: "20%", left: "5%", pointerEvents: "none" }} />

      {/* ══ Floating illustrations ══ */}
      <BitcoinSVG style={{ top: "8%", left: "8%", animation: "floatSlow 4s ease-in-out infinite", zIndex: 2 }} />
      <AirpodsSVG style={{ top: "12%", right: "6%", animation: "floatSlow 5s ease-in-out 0.5s infinite", transform: "rotate(15deg)", zIndex: 2 }} />
      <UkraineFlagSVG style={{ bottom: "12%", left: "10%", animation: "floatSlow 4.5s ease-in-out 1s infinite", zIndex: 2 }} />

      {/* Currency scale (€ vs $) — bottom center-left */}
      <svg style={{ position: "absolute", bottom: "14%", left: "18%", pointerEvents: "none", opacity: 0.85, animation: "floatSlow 5s ease-in-out 1.5s infinite", zIndex: 2 }} width="110" height="80" viewBox="0 0 110 80">
        {/* Scale base */}
        <rect x="48" y="40" width="14" height="36" rx="3" fill="#B0B8C4" opacity="0.5"/>
        <ellipse cx="55" cy="76" rx="22" ry="4" fill="#CBD5E1" opacity="0.4"/>
        {/* Scale beam */}
        <rect x="10" y="38" width="90" height="4" rx="2" fill="#94A3B8" transform="rotate(-5 55 40)"/>
        {/* Euro side (higher = lighter) */}
        <text x="18" y="34" fontSize="28" fontWeight="800" fill="#FFB800" fontFamily="Arial" style={{filter:"drop-shadow(0 2px 4px rgba(0,0,0,0.15))"}}>€</text>
        {/* Dollar side (lower = heavier) */}
        <text x="78" y="42" fontSize="28" fontWeight="800" fill="#FFB800" fontFamily="Arial" style={{filter:"drop-shadow(0 2px 4px rgba(0,0,0,0.15))"}}>$</text>
      </svg>

      {/* Call center girl — right side */}
      <svg style={{ position: "absolute", bottom: "5%", right: "3%", pointerEvents: "none", zIndex: 2, opacity: 0.9 }} width="160" height="200" viewBox="0 0 160 200">
        {/* Laptop */}
        <rect x="20" y="145" width="80" height="50" rx="4" fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="1.5"/>
        <rect x="25" y="148" width="70" height="38" rx="2" fill="#F8FAFC"/>
        <circle cx="60" cy="167" r="4" fill="#CBD5E1"/>
        <rect x="15" y="194" width="90" height="4" rx="2" fill="#CBD5E1"/>
        {/* Body */}
        <ellipse cx="95" cy="165" rx="25" ry="30" fill="#BFDBFE"/>
        {/* Neck */}
        <rect x="88" y="110" width="14" height="15" rx="5" fill="#FDDEB5"/>
        {/* Head */}
        <ellipse cx="95" cy="90" rx="24" ry="28" fill="#FDDEB5"/>
        {/* Hair */}
        <ellipse cx="95" cy="72" rx="26" ry="18" fill="#8B6914"/>
        <ellipse cx="75" cy="90" rx="8" ry="25" fill="#A07420"/>
        <ellipse cx="115" cy="90" rx="8" ry="25" fill="#A07420"/>
        <ellipse cx="120" cy="115" rx="6" ry="18" fill="#A07420"/>
        <ellipse cx="70" cy="115" rx="6" ry="18" fill="#A07420"/>
        {/* Eyes */}
        <ellipse cx="86" cy="88" rx="3.5" ry="4" fill="#FFF"/>
        <ellipse cx="104" cy="88" rx="3.5" ry="4" fill="#FFF"/>
        <circle cx="87" cy="89" r="2" fill="#4A3520"/>
        <circle cx="105" cy="89" r="2" fill="#4A3520"/>
        <circle cx="87.5" cy="88" r="0.8" fill="#FFF"/>
        <circle cx="105.5" cy="88" r="0.8" fill="#FFF"/>
        {/* Smile */}
        <path d="M89 98 Q95 104 101 98" fill="none" stroke="#D4845A" strokeWidth="1.5" strokeLinecap="round"/>
        {/* Headset */}
        <path d="M70 82 Q70 60 95 58 Q120 60 120 82" fill="none" stroke="#334155" strokeWidth="3" strokeLinecap="round"/>
        <rect x="65" y="80" width="10" height="16" rx="5" fill="#334155"/>
        <rect x="115" y="80" width="10" height="16" rx="5" fill="#334155"/>
        {/* Mic arm */}
        <path d="M68 92 Q60 100 65 108" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="65" cy="110" r="4" fill="#334155"/>
        {/* Arm on laptop */}
        <path d="M80 155 Q70 140 55 148" fill="none" stroke="#FDDEB5" strokeWidth="8" strokeLinecap="round"/>
      </svg>

      {/* Sparkles and dots scattered around */}
      <Sparkle x="15%" y="25%" size={16} color="#FFD700" delay={0} />
      <Sparkle x="85%" y="30%" size={12} color="#38BDF8" delay={0.5} />
      <Sparkle x="75%" y="70%" size={14} color="#A78BFA" delay={1} />
      <Sparkle x="20%" y="75%" size={10} color="#38BDF8" delay={1.5} />
      <Sparkle x="50%" y="10%" size={12} color="#FFD700" delay={0.8} />
      <Sparkle x="90%" y="55%" size={10} color="#F472B6" delay={2} />
      <Dot x="25%" y="15%" size={8} color="#38BDF8" />
      <Dot x="80%" y="20%" size={6} color="#FFD700" />
      <Dot x="12%" y="60%" size={10} color="#A78BFA" />
      <Dot x="88%" y="75%" size={7} color="#38BDF8" />
      <Dot x="45%" y="85%" size={6} color="#FFD700" />
      <Dot x="70%" y="8%" size={9} color="#F472B6" />

      {/* ══ Login Card ══ */}
      <div style={{ width: 440, maxWidth: "92vw", background: "rgba(15,23,42,0.85)", backdropFilter: "blur(24px) saturate(180%)", WebkitBackdropFilter: "blur(24px) saturate(180%)", borderRadius: 24, border: "1px solid rgba(99,102,241,0.25)", padding: "44px 40px", boxShadow: "0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1) inset", animation: "fadeSlideUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)", position: "relative", zIndex: 10 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          {I.logo}
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 24, background: "linear-gradient(135deg, #38BDF8, #A78BFA)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", letterSpacing: -0.5 }}>Blitz CRM</span>
          <span style={{ fontSize: 11, color: "#0EA5E9", fontFamily: "'JetBrains Mono',monospace", background: "#EFF6FF", padding: "3px 12px", borderRadius: 20, border: "1px solid #BFDBFE", fontWeight: 600 }}>v{VERSION}</span>
        </div>

        {/* Server status */}
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 24, padding: "8px 14px", borderRadius: 10,
          background: serverStatus === "online" ? "#ECFDF5" : serverStatus === "offline" ? "#FEF3C7" : "#F8FAFC",
          border: `1px solid ${serverStatus === "online" ? "#A7F3D0" : serverStatus === "offline" ? "#FDE68A" : "#E2E8F0"}` }}>
          <div style={{ width: 9, height: 9, borderRadius: "50%",
            background: serverStatus === "online" ? "#10B981" : serverStatus === "offline" ? "#F59E0B" : "#94A3B8",
            boxShadow: serverStatus === "online" ? "0 0 8px rgba(16,185,129,0.5)" : "none",
            animation: serverStatus === "online" ? "pulse 2s ease-in-out infinite" : "none" }} />
          <span style={{ fontSize: 13, color: serverStatus === "online" ? "#065F46" : serverStatus === "offline" ? "#92400E" : "#64748B", fontWeight: 600 }}>
            {serverStatus === "online" ? "Server connected" : serverStatus === "banned" ? "🚫 Your IP is blocked — contact administrator" : serverStatus === "offline" ? "Server offline — offline login available" : "Checking server..."}
          </span>
        </div>

        <form onSubmit={submit} method="post" autoComplete="on">
          <label htmlFor="login-email" style={{ display: "block", color: "#94A3B8", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Email</label>
          <input id="login-email" name="email" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
            style={{ width: "100%", padding: "14px 18px", background: "rgba(30,41,59,0.8)", border: "2px solid rgba(99,102,241,0.2)", borderRadius: 14, color: "#F1F5F9", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 20, transition: "all 0.2s ease", caretColor: "#818CF8" }}
            onFocus={e => { e.target.style.borderColor = "#38BDF8"; e.target.style.boxShadow = "0 0 0 4px rgba(56,189,248,0.1)"; }}
            onBlur={e => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }} />

          <label htmlFor="login-password" style={{ display: "block", color: "#334155", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Password</label>
          <div style={{ position: "relative", marginBottom: 20 }}>
            <input id="login-password" name="password" type={showPass ? "text" : "password"} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
              style={{ width: "100%", padding: "14px 18px", paddingRight: 50, background: "rgba(30,41,59,0.8)", border: "2px solid rgba(99,102,241,0.2)", borderRadius: 14, color: "#F1F5F9", fontSize: 15, outline: "none", boxSizing: "border-box", transition: "all 0.2s ease", caretColor: "#818CF8" }}
              onFocus={e => { e.target.style.borderColor = "#38BDF8"; e.target.style.boxShadow = "0 0 0 4px rgba(56,189,248,0.1)"; }}
              onBlur={e => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }} />
            <button type="button" onClick={() => setShowPass(!showPass)} tabIndex={-1}
              style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: showPass ? "#0EA5E9" : "#94A3B8", transition: "color 0.2s" }}>
              {showPass
                ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              }
            </button>
          </div>

          {error && <div style={{ color: "#DC2626", fontSize: 13, marginBottom: 16, padding: "10px 14px", background: "#FEF2F2", borderRadius: 12, border: "1px solid #FECACA", fontWeight: 500 }}>{renderTelegramNotInitiated(error) || error}</div>}

          {/* DEBUG PANEL */}
          {debugData && (
            <div style={diagStyle}>
              <div style={{ color: "#92400E", fontWeight: 700, marginBottom: 8 }}>🔍 DEBUG DIAGNOSTIC (v{VERSION})</div>
              <div>Server version: {debugData.version || debugData.v || "unknown (old server?)"}</div>
              <div>Data dir: {debugData.dataDir || "?"}</div>
              <div>users.json exists: {debugData.fileExists ? "✅ YES" : "❌ NO"}</div>
              <div>─────────────────────────</div>
              <div>Users in file: {debugData.fileUsersCount ?? "?"}</div>
              <div style={{ color: debugData.fileUsersWithHash === debugData.fileUsersCount ? "#065F46" : "#DC2626" }}>
                With passwordHash: {debugData.fileUsersWithHash ?? "?"} / {debugData.fileUsersCount ?? "?"}
              </div>
              {debugData.fileUsersNoHash && debugData.fileUsersNoHash.length > 0 && (
                <div style={{ color: "#DC2626" }}>⚠️ MISSING HASH: {debugData.fileUsersNoHash.join(", ")}</div>
              )}
              <div>─────────────────────────</div>
              <div>Email in file: {debugData.emailInFile ? "✅" : "❌"}</div>
              <div>Email in seed: {debugData.emailInSeed ? "✅" : "❌"}</div>
              <div>File has hash: {debugData.fileHasHash ? "✅" : "❌ ← PROBLEM"}</div>
              <div>Seed has hash: {debugData.seedHasHash ? "✅" : "❌"}</div>
              <div style={{ color: debugData.hashMatchFile || debugData.hashMatchSeed ? "#065F46" : "#DC2626" }}>
                Hash match (file): {debugData.hashMatchFile ? "✅ MATCH" : "❌ NO MATCH"}
              </div>
              <div style={{ color: debugData.hashMatchSeed ? "#065F46" : "#DC2626" }}>
                Hash match (seed): {debugData.hashMatchSeed ? "✅ MATCH" : "❌ NO MATCH"}
              </div>
              <div>─────────────────────────</div>
              <div>Server hash (file): {debugData.serverHash8 || "NONE"}</div>
              <div>Server hash (seed): {debugData.seedHash8 || "NONE"}</div>
              <div>Client hash: {debugData.clientHash8 || "?"}</div>
              {debugData.clientHashFull && <div>Client full: {debugData.clientHashFull}</div>}
            </div>
          )}

          <button type="submit" disabled={loading || blocked > 0 || serverStatus === "banned"}
            style={{ width: "100%", padding: 16, background: loading ? "rgba(99,102,241,0.4)" : "linear-gradient(135deg, #6366F1 0%, #818CF8 50%, #A78BFA 100%)", color: "#FFF", border: "none", borderRadius: 14, fontSize: 16, fontWeight: 800, cursor: loading ? "wait" : "pointer", boxShadow: loading ? "none" : "0 6px 30px rgba(99,102,241,0.45)", transition: "all 0.2s", letterSpacing: 0.5, marginTop: 8 }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}
          >
            {loading ? "Signing in..." : serverStatus === "banned" ? "🚫 Blocked" : "Sign In →"}
          </button>
        </form>

        {/* Open in App */}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <a href={window.location.href}
            onClick={(e) => {
              e.preventDefault();
              if (/iPhone|iPad|Android/i.test(navigator.userAgent)) {
                alert("To open as app:\n\niPhone/iPad: Tap Share → Add to Home Screen\nAndroid: Tap ⋮ → Add to Home Screen");
              } else {
                window.open(window.location.href, '_blank', 'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=420,height=800');
              }
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 14, color: "#475569", fontSize: 13, fontWeight: 600, textDecoration: "none", cursor: "pointer", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
            Open in App
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
